import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import { Rating } from '@/domain/Models';
import type { SentenceSessionView } from '@/domain/Repositories';

export function SentencePracticePage() {
  const [session, setSession] = useState<SentenceSessionView | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const id = await appServices.sentences.startOrResumeSession();
      setSession(await appServices.sentences.session(id));
      setError(null);
    } catch (e) {
      setError(String(e).replace(/^Error:\s*/, ''));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="page stack">
        <div className="error-box">{error}</div>
        <button type="button" className="secondary" onClick={() => navigate('/scenario')}>
          返回
        </button>
      </div>
    );
  }
  if (done) {
    return (
      <div className="page stack">
        <h2>本轮例句练习完成！</h2>
        <button type="button" onClick={() => navigate('/scenario')}>
          返回
        </button>
      </div>
    );
  }
  if (!session?.example) return <div className="page muted">加载中…</div>;

  return (
    <div className="page stack">
      <div className="muted">
        {session.currentIndex + 1} / {session.totalCount}
      </div>
      <section className="card stack">
        <div>{session.example.textZh}</div>
        <div className="muted">{session.example.scene}</div>
      </section>
      {session.answerRevealed ? (
        <section className="card stack">
          <strong>{session.example.textEn}</strong>
          {session.example.audioAssetId && (
            <button
              type="button"
              className="secondary"
              onClick={() => void appServices.audio.play(session.example!.audioAssetId, 1)}
            >
              朗读
            </button>
          )}
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={() => void rate(Rating.AGAIN)}
            >
              陌生
            </button>
            <button type="button" onClick={() => void rate(Rating.HARD)}>
              模糊
            </button>
            <button type="button" onClick={() => void rate(Rating.GOOD)}>
              掌握
            </button>
          </div>
        </section>
      ) : (
        <button type="button" onClick={() => void reveal()}>
          显示答案
        </button>
      )}
    </div>
  );

  async function reveal() {
    await appServices.sentences.revealAnswer(session!.sessionId);
    setSession(await appServices.sentences.session(session!.sessionId));
  }

  async function rate(rating: Rating) {
    const result = await appServices.sentences.recordReview(
      session!.sessionId,
      session!.example!.id,
      session!.currentIndex,
      rating,
    );
    if (result.done) {
      setDone(true);
      return;
    }
    setSession(await appServices.sentences.session(session!.sessionId));
  }
}
